import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';

import {catchError, map, switchMap, tap, withLatestFrom} from 'rxjs/operators';
import {EMPTY, of} from 'rxjs';

import {AppState} from '@store/app.states';

import {PublicationService} from '../../publications.service';
import {
  AutoArchiveFailure, ClonePublication, ClonePublicationFailure,
  DeletePublication,
  DeletePublicationFailure,
  DeletePublicationSuccess,
  PublicationIsSaved,
  PublicationsActionTypes,
  RefreshList,
  RefreshListFailure,
  RefreshListSuccess,
  MultiChangePublishedStatus,
} from './publications.actions';
import {
  ActivePublicationActionTypes,
  SavePublication,
} from '@app/components/publications/create-publication/_store/active-publication.actions';
import {getLastRefresh, getPublicationsList} from '@app/components/publications/store/publications/publications.selector';
import {ToastrService} from 'ngx-toastr';
import {JsonPipe} from '@angular/common';
import {Router} from '@angular/router';
import {AuthService} from '@app/services/auth/auth.service';

@Injectable()
export class PublicationsEffect {
  constructor(
    private actions: Actions,
    private store: Store<AppState>,
    private publicationaService: PublicationService,
    private auth: AuthService,
    private toastr: ToastrService,
    private jsonPipe: JsonPipe,
    public router: Router,
  ) {
  }

  // @Effect()
  // refreshList = this.actions.pipe(
  //   ofType(PublicationsActionTypes.RefreshList),
  //   map((action: RefreshList) => action.payload),
  //   switchMap(payload => {
  //     return this.publicationaService.getPublicationsList(payload.nbMonths)
  //       .pipe(
  //         map(data => new RefreshListSuccess({list: data.data.list})),
  //         catchError(error =>
  //           of(new RefreshListFailure({error}))
  //         )
  //       );
  //   }),
  // );


  refreshListTwo = createEffect(() => this.actions.pipe(
    ofType(PublicationsActionTypes.RefreshList),
    map((action: RefreshList) => action.payload),
    withLatestFrom(
      this.store.select(getPublicationsList),
      this.store.select(getLastRefresh)
    ),
    switchMap(([payload, publications, lastRefresh]) => {
      // Durée de cache : 5 minutes (en millisecondes)
      const CACHE_DURATION = 5 * 60 * 1000;
      const now = Date.now();
      const isCacheValid = lastRefresh && (now - lastRefresh) < CACHE_DURATION;

      // console.log('Should I refresh list ?', {
      //   publicationsCount: publications?.length,
      //   forceRefresh: payload.forceRefresh,
      //   isCacheValid,
      //   cacheAge: lastRefresh ? Math.round((now - lastRefresh) / 1000) : null
      // });

      // Utiliser le cache si :
      // - On a des données (même une seule publication)
      // - Le cache est valide (< 5 minutes)
      // - Ce n'est pas un forceRefresh
      if (publications?.length >= 0 && isCacheValid && !payload.forceRefresh) {
        return of(new RefreshListSuccess({list: publications}));
      }

      // Sinon, charger depuis le serveur
      return this.publicationaService.getPublicationsList(payload.nbMonths).pipe(
        map((data) => new RefreshListSuccess({list: data.data.list})),
        catchError((error) => of(new RefreshListFailure({error}))),
      );
    }),
  ));


  deletePublication = createEffect(() => this.actions.pipe(
    ofType(PublicationsActionTypes.DeletePublication),
    map((action: DeletePublication) => action.payload),
    switchMap((payload) => {
      return this.publicationaService.deletePublication(payload.uuid).pipe(
        map((data) => new DeletePublicationSuccess({uuid: data.data.uuid})),
        catchError((error) => of(new DeletePublicationFailure({error}))),
      );
    }),
  ));


  publicationSaved = createEffect(() => this.actions.pipe(
    ofType(ActivePublicationActionTypes.SAVE_PUBLICATION),
    map((action: SavePublication) => action.payload),
    switchMap((payload) => {
      return of(new PublicationIsSaved({publication: payload.publication}));
    }),
  ));


  AutoArchive = createEffect(() => this.actions.pipe(
    ofType(PublicationsActionTypes.AutoArchive),
    map((action: RefreshList) => action.payload),
    // withLatestFrom(this.store.select(getPublicationsList)),
    // map((state) => state),
    switchMap((payload) => {
      return this.publicationaService.AutoArchive().pipe(
        map((data) => {

          this.toastr.info(
            `${this.jsonPipe.transform(data.data.datas.result)}`,
            'Batch update OK!',
            {
              timeOut: 7000,
              progressBar: true,
              enableHtml: true,
            });


          return new RefreshList({forceRefresh: true, nbMonths: 6});
        }),
        catchError((error) => of(new AutoArchiveFailure({error}))),
      );
    }),
  ));


  ClonePublication = createEffect(() => this.actions.pipe(
    ofType(PublicationsActionTypes.ClonePublication),
    map((action: ClonePublication) => action.payload),
    // withLatestFrom(this.store.select(getPublicationsList)),
    // map((state) => state),
    switchMap((payload) => {
      return this.publicationaService.clonePublication(payload.pubUuids, this.auth.getUsername()).pipe(
        map((data) => {

          console.log(data.data.result.nbSaved);
          console.log(data.data.result.newUuids);

          const newPubUuids: string[] = data.data.datas.result.newUuids;

          this.toastr.info(
            `${this.jsonPipe.transform(data.data.datas.result)}`,
            'Publication(s) cloned!',
            {
              timeOut: 7000,
              progressBar: true,
              enableHtml: true,
            });

          if (newPubUuids.length === 1) {
            this.router.navigateByUrl(`publications/edit/${newPubUuids}`);
          } else {
            return new RefreshList({forceRefresh: true, nbMonths: 6});
          }

          // return of(EMPTY);

        }),
        catchError((error) => of(new ClonePublicationFailure({error}))),
      );
    }),
  ));


  //
  //
  //

  MultiStop = createEffect(() => this.actions.pipe(
    ofType(PublicationsActionTypes.MultiChangePublishedStatus),
    map((action: MultiChangePublishedStatus) => action.payload),
    // withLatestFrom(this.store.select(getPublicationsList)),
    // map((state) => state),
    switchMap((payload) => {
      return this.publicationaService.MultiChangePublishedStatus(payload.uuids, payload.published).pipe(
        map((data) => {

          this.toastr.info(
            `${this.jsonPipe.transform(data.data.datas.result)}`,
            'Batch stop OK!',
            {
              timeOut: 7000,
              progressBar: true,
              enableHtml: true,
            });


          return new RefreshList({forceRefresh: true, nbMonths: 6});
        }),
        catchError((error) => of(new AutoArchiveFailure({error}))),
      );
    }),
  ));


  //
  //
  //


}
